import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
  getCompany,
  resolveCompanyId,
  shouldRedirectForCompany,
  writeStoredCompanyId,
} from '../branding/companies';

const CompanyContext = createContext({
  companyId: 'fabworks',
  company: getCompany('fabworks'),
  switchCompany: () => {},
});

export const useCompany = () => useContext(CompanyContext);

export const CompanyProvider = ({ children }) => {
  const [companyId, setCompanyId] = useState(resolveCompanyId);

  const company = useMemo(() => getCompany(companyId), [companyId]);

  useEffect(() => {
    writeStoredCompanyId(companyId);
  }, [companyId]);

  useEffect(() => {
    if (typeof document === 'undefined') return;
    document.title = company.shortName;
    const favicon = document.querySelector("link[rel='icon']");
    if (favicon && company.logos?.favicon) favicon.href = company.logos.favicon;
    const appleIcon = document.querySelector("link[rel='apple-touch-icon']");
    if (appleIcon && company.logos?.favicon) appleIcon.href = company.logos.favicon;
    const desc = document.querySelector("meta[name='description']");
    if (desc) desc.content = `${company.shortName} - ${company.tagline}`;
    const themeColor = document.querySelector("meta[name='theme-color']");
    if (themeColor && company.id === 'langley') themeColor.content = '#141414';
    else if (themeColor && company.id === 'whw') themeColor.content = '#8B0000';
    else if (themeColor && company.id === 'fabworks') themeColor.content = '#0B1F33';
  }, [company]);

  useEffect(() => {
    const sync = () => setCompanyId(resolveCompanyId());
    window.addEventListener('storage', sync);
    window.addEventListener('wm-company-change', sync);
    return () => {
      window.removeEventListener('storage', sync);
      window.removeEventListener('wm-company-change', sync);
    };
  }, []);

  const switchCompany = useCallback((nextId) => {
    const target = getCompany(nextId);
    if (shouldRedirectForCompany(target)) {
      writeStoredCompanyId(nextId);
      window.location.href = target.workUrl;
      return;
    }
    writeStoredCompanyId(nextId);
    setCompanyId(nextId);
    window.dispatchEvent(new Event('wm-company-change'));
  }, []);

  const value = useMemo(
    () => ({ companyId, company, switchCompany }),
    [companyId, company, switchCompany]
  );

  return <CompanyContext.Provider value={value}>{children}</CompanyContext.Provider>;
};

export default CompanyProvider;
